import { supabase } from '../lib/supabase';

export class GeographicAnalyticsService {
  // Enhanced network error detection
  static isNetworkError(error) {
    return (
      error?.message?.includes('NetworkError') ||
      error?.message?.includes('fetch') ||
      error?.message?.includes('network') ||
      error?.code === 'NETWORK_ERROR' ||
      error?.name === 'NetworkError' ||
      !navigator?.onLine
    );
  }

  // Enhanced connection validation
  static async validateConnection() {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized. Please check your environment variables.');
      }

      // Test basic connectivity with a simple query
      const { error } = await supabase?.from('profiles')?.select('count')?.limit(1);
      
      if (error && this.isNetworkError(error)) {
        throw new Error('Network connection failed. Please check your internet connection and Supabase configuration.');
      }

      return { isConnected: true, error: null };
    } catch (error) {
      console.warn('Connection validation failed:', error);
      return { 
        isConnected: false, 
        error: this.isNetworkError(error) ? 
          'Network connection failed. Please check your internet connection.' : error?.message ||'Connection validation failed'
      };
    }
  }

  // Get geographic performance data from actual QR scan data
  static async getGeographicPerformance(filters = {}) {
    try {
      // Validate connection first
      const { isConnected, error: connectionError } = await this.validateConnection();
      if (!isConnected) {
        console.warn('Connection validation failed, returning sample data');
        return {
          data: this.getSampleGeographicData(),
          error: connectionError
        };
      }

      const { dateRange, metric = 'scans', deviceType, campaign } = filters;
      
      // Base query for QR scans with geographic data
      let query = supabase?.from('qr_scans')?.select(`
        id,
        country_code,
        country_name,
        city,
        device_type,
        converted,
        scanned_at,
        qr_code_id,
        qr_codes!inner (
          id,
          name,
          campaign_id,
          campaigns (
            id,
            name
          )
        )
      `);

      // Apply filters
      if (dateRange) {
        query = query?.gte('scanned_at', dateRange);
      }

      if (deviceType && deviceType !== 'all') {
        query = query?.eq('device_type', deviceType);
      }

      // Execute query with timeout
      const { data, error } = await Promise.race([
        query?.limit(10000),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 15000)
        )
      ]);

      if (error) throw error;

      // Process geographic data
      const geographicData = this.processGeographicData(data || [], metric);

      return {
        data: geographicData,
        error: null
      };
    } catch (error) {
      console.error('Failed to fetch geographic performance:', error);
      
      // Enhanced error handling
      if (this.isNetworkError(error)) {
        return {
          data: this.getSampleGeographicData(),
          error: 'Network connection failed. Showing sample data for development purposes.'
        };
      }

      return {
        data: this.getSampleGeographicData(),
        error: error?.message || 'Failed to fetch geographic performance. Showing sample data.'
      };
    }
  }

  // Get country-level performance data with enhanced error handling
  static async getCountryPerformance(filters = {}) {
    try {
      // Validate connection first
      const { isConnected, error: connectionError } = await this.validateConnection();
      if (!isConnected) {
        console.warn('Network error detected. Returning sample country data for development.');
        return {
          data: this.getSampleCountryData(),
          error: connectionError
        };
      }

      const { dateRange, metric = 'scans' } = filters;
      
      let query = supabase?.from('qr_scans')?.select(`
        country_code,
        country_name,
        device_type,
        converted,
        scanned_at
      `);

      if (dateRange) {
        query = query?.gte('scanned_at', dateRange);
      }

      // Execute query with timeout
      const { data, error } = await Promise.race([
        query,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 15000)
        )
      ]);

      if (error) throw error;

      // If no data available, return sample data for development
      if (!data || data?.length === 0) {
        console.warn('No QR scan data available. Returning sample data for development.');
        return {
          data: this.getSampleCountryData(),
          error: null
        };
      }

      // Group by country and calculate metrics
      const countryStats = {};
      
      (data || [])?.forEach(scan => {
        const country = scan?.country_name || 'Unknown';
        const countryCode = scan?.country_code || 'XX';
        
        if (!countryStats?.[country]) {
          countryStats[country] = {
            name: country,
            code: countryCode,
            totalScans: 0,
            conversions: 0,
            deviceBreakdown: {},
            conversionRate: 0
          };
        }

        countryStats[country].totalScans++;
        
        if (scan?.converted) {
          countryStats[country].conversions++;
        }

        // Device breakdown
        const device = scan?.device_type || 'unknown';
        countryStats[country].deviceBreakdown[device] = (countryStats?.[country]?.deviceBreakdown?.[device] || 0) + 1;
      });

      // Calculate conversion rates and sort
      const countryArray = Object.values(countryStats)?.map(country => ({
        ...country,
        conversionRate: country?.totalScans > 0 ? (country?.conversions / country?.totalScans * 100) : 0
      }))?.sort((a, b) => b?.totalScans - a?.totalScans);

      return {
        data: countryArray,
        error: null
      };
    } catch (error) {
      console.error('Failed to fetch country performance:', error);
      
      // Enhanced network error handling
      if (this.isNetworkError(error)) {
        console.warn('Network error detected. Returning sample country data for development.');
        return {
          data: this.getSampleCountryData(),
          error: 'Network connection failed. Showing sample data for development purposes.'
        };
      }

      return {
        data: this.getSampleCountryData(),
        error: error?.message || 'Failed to fetch country performance. Showing sample data.'
      };
    }
  }

  // Get city-level performance data with enhanced error handling
  static async getCityPerformance(filters = {}) {
    try {
      // Validate connection first
      const { isConnected, error: connectionError } = await this.validateConnection();
      if (!isConnected) {
        console.warn('Network error detected. Returning sample city data for development.');
        return {
          data: this.getSampleCityData(),
          error: connectionError
        };
      }

      const { dateRange, limit = 20 } = filters;
      
      let query = supabase?.from('qr_scans')?.select(`
        city,
        country_name,
        country_code,
        converted,
        device_type,
        scanned_at
      `);

      if (dateRange) {
        query = query?.gte('scanned_at', dateRange);
      }

      // Execute query with timeout
      const { data, error } = await Promise.race([
        query,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 15000)
        )
      ]);

      if (error) throw error;

      // If no data available, return sample data for development
      if (!data || data?.length === 0) {
        console.warn('No QR scan data available. Returning sample city data for development.');
        return {
          data: this.getSampleCityData(),
          error: null
        };
      }

      // Group by city
      const cityStats = {};
      
      (data || [])?.forEach(scan => {
        const city = scan?.city || 'Unknown City';
        const country = scan?.country_name || 'Unknown Country';
        const cityKey = `${city}, ${country}`;
        
        if (!cityStats?.[cityKey]) {
          cityStats[cityKey] = {
            city,
            country,
            countryCode: scan?.country_code || 'XX',
            totalScans: 0,
            conversions: 0,
            conversionRate: 0
          };
        }

        cityStats[cityKey].totalScans++;
        
        if (scan?.converted) {
          cityStats[cityKey].conversions++;
        }
      });

      // Calculate conversion rates and sort
      const cityArray = Object.values(cityStats)?.map(city => ({
        ...city,
        conversionRate: city?.totalScans > 0 ? (city?.conversions / city?.totalScans * 100) : 0
      }))?.sort((a, b) => b?.totalScans - a?.totalScans)?.slice(0, limit);

      return {
        data: cityArray,
        error: null
      };
    } catch (error) {
      console.error('Failed to fetch city performance:', error);
      
      // Enhanced network error handling
      if (this.isNetworkError(error)) {
        console.warn('Network error detected. Returning sample city data for development.');
        return {
          data: this.getSampleCityData(),
          error: 'Network connection failed. Showing sample data for development purposes.'
        };
      }

      return {
        data: this.getSampleCityData(),
        error: error?.message || 'Failed to fetch city performance. Showing sample data.'
      };
    }
  }

  // Sample data for development when database is not available
  static getSampleCountryData() {
    return [
      {
        name: 'United States',
        code: 'US',
        totalScans: 2847,
        conversions: 456,
        conversionRate: 16.0,
        deviceBreakdown: { mobile: 2200, desktop: 647 }
      },
      {
        name: 'Germany',
        code: 'DE',
        totalScans: 1923,
        conversions: 312,
        conversionRate: 16.2,
        deviceBreakdown: { mobile: 1455, desktop: 468 }
      },
      {
        name: 'United Kingdom',
        code: 'GB',
        totalScans: 1654,
        conversions: 298,
        conversionRate: 18.0,
        deviceBreakdown: { mobile: 1203, desktop: 451 }
      },
      {
        name: 'France',
        code: 'FR',
        totalScans: 1432,
        conversions: 223,
        conversionRate: 15.6,
        deviceBreakdown: { mobile: 1089, desktop: 343 }
      },
      {
        name: 'Canada',
        code: 'CA',
        totalScans: 1187,
        conversions: 189,
        conversionRate: 15.9,
        deviceBreakdown: { mobile: 912, desktop: 275 }
      },
      {
        name: 'Australia',
        code: 'AU',
        totalScans: 943,
        conversions: 167,
        conversionRate: 17.7,
        deviceBreakdown: { mobile: 734, desktop: 209 }
      },
      {
        name: 'Japan',
        code: 'JP',
        totalScans: 876,
        conversions: 134,
        conversionRate: 15.3,
        deviceBreakdown: { mobile: 687, desktop: 189 }
      },
      {
        name: 'Netherlands',
        code: 'NL',
        totalScans: 654,
        conversions: 98,
        conversionRate: 15.0,
        deviceBreakdown: { mobile: 523, desktop: 131 }
      }
    ];
  }

  // Sample city data for development
  static getSampleCityData() {
    return [
      {
        city: 'Rotterdam',
        country: 'Netherlands',
        countryCode: 'NL',
        totalScans: 5,
        conversions: 0,
        conversionRate: 0
      },
      {
        city: 'Amsterdam',
        country: 'Netherlands',
        countryCode: 'NL',
        totalScans: 4,
        conversions: 0,
        conversionRate: 0
      },
      {
        city: 'Bastia',
        country: 'France',
        countryCode: 'FR',
        totalScans: 2,
        conversions: 0,
        conversionRate: 0
      },
      {
        city: 'Unknown City',
        country: 'Unknown Country',
        countryCode: 'XX',
        totalScans: 1,
        conversions: 0,
        conversionRate: 0
      }
    ];
  }

  // Sample geographic data for development
  static getSampleGeographicData() {
    return {
      summary: {
        totalScans: 12500,
        totalCountries: 8,
        totalCities: 25,
        conversionRate: 16.2
      },
      countries: {
        'United States': { name: 'United States', code: 'US', scans: 2847, conversions: 456 },
        'Germany': { name: 'Germany', code: 'DE', scans: 1923, conversions: 312 },
        'United Kingdom': { name: 'United Kingdom', code: 'GB', scans: 1654, conversions: 298 },
        'Netherlands': { name: 'Netherlands', code: 'NL', scans: 1245, conversions: 198 },
        'France': { name: 'France', code: 'FR', scans: 987, conversions: 156 },
        'Canada': { name: 'Canada', code: 'CA', scans: 756, conversions: 123 },
        'Japan': { name: 'Japan', code: 'JP', scans: 634, conversions: 98 },
        'Australia': { name: 'Australia', code: 'AU', scans: 543, conversions: 87 }
      },
      cities: {
        'New York, United States': { city: 'New York', country: 'United States', scans: 743, conversions: 125 },
        'London, United Kingdom': { city: 'London', country: 'United Kingdom', scans: 612, conversions: 98 }
      },
      regions: {
        'North America': { scans: 4034, conversions: 645 },
        'Europe': { scans: 6231, conversions: 987 },
        'Asia': { scans: 1876, conversions: 287 },
        'Others': { scans: 359, conversions: 54 }
      }
    };
  }

  // Get geographic tracking status with enhanced error handling
  static async getGeographicTrackingStatus() {
    try {
      // Validate connection first
      const { isConnected, error: connectionError } = await this.validateConnection();
      if (!isConnected) {
        return {
          data: {
            isActive: false,
            error: connectionError,
            totalCountries: 8,
            totalCities: 15,
            countriesWithData: ['US', 'GB', 'DE', 'FR', 'CA', 'AU', 'JP', 'NL'],
            citiesWithData: ['New York', 'London', 'Berlin', 'Paris', 'Toronto'],
            trackingCapabilities: {
              countryTracking: true,
              cityTracking: true,
              deviceTracking: true,
              realTimeTracking: true
            }
          },
          error: connectionError
        };
      }

      // Check if we have recent geographic data
      const { data, error } = await Promise.race([
        supabase?.from('qr_scans')?.select(`
          id,
          country_code,
          country_name,
          city,
          scanned_at
        `)?.not('country_code', 'is', null)?.order('scanned_at', { ascending: false })?.limit(10),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        )
      ]);

      if (error) throw error;

      const hasGeographicData = data && data?.length > 0;
      const lastGeographicScan = hasGeographicData ? new Date(data?.[0]?.scanned_at) : null;
      const uniqueCountries = [...new Set(data?.map(scan => scan?.country_name)?.filter(Boolean))];
      const uniqueCities = [...new Set(data?.map(scan => scan?.city)?.filter(Boolean))];

      return {
        data: {
          isActive: hasGeographicData,
          lastScanWithLocation: lastGeographicScan,
          totalCountries: uniqueCountries?.length || 0,
          totalCities: uniqueCities?.length || 0,
          countriesWithData: uniqueCountries,
          citiesWithData: uniqueCities?.slice(0, 10), // Top 10 cities
          trackingCapabilities: {
            countryTracking: true,
            cityTracking: true,
            deviceTracking: true,
            realTimeTracking: true
          }
        },
        error: null
      };
    } catch (error) {
      console.error('Failed to check geographic tracking status:', error);
      
      // Return sample status for development
      return {
        data: {
          isActive: false,
          error: error?.message,
          totalCountries: 8,
          totalCities: 15,
          countriesWithData: ['US', 'GB', 'DE', 'FR', 'CA', 'AU', 'JP', 'NL'],
          citiesWithData: ['New York', 'London', 'Berlin', 'Paris', 'Toronto'],
          trackingCapabilities: {
            countryTracking: true,
            cityTracking: true,
            deviceTracking: true,
            realTimeTracking: true
          }
        },
        error: this.isNetworkError(error) ? 
          'Network connection failed. Showing sample tracking status.' : error?.message ||'Failed to check tracking status.'
      };
    }
  }

  // Helper method to process geographic data
  static processGeographicData(scans, metric) {
    const processed = {
      summary: {
        totalScans: scans?.length || 0,
        totalCountries: 0,
        totalCities: 0,
        conversionRate: 0
      },
      countries: {},
      cities: {},
      regions: {
        'North America': { scans: 0, conversions: 0 },
        'Europe': { scans: 0, conversions: 0 },
        'Asia': { scans: 0, conversions: 0 },
        'Others': { scans: 0, conversions: 0 }
      }
    };

    scans?.forEach(scan => {
      const country = scan?.country_name || 'Unknown';
      const city = scan?.city || 'Unknown';
      
      // Country stats
      if (!processed?.countries?.[country]) {
        processed.countries[country] = {
          name: country,
          code: scan?.country_code || 'XX',
          scans: 0,
          conversions: 0
        };
      }
      processed.countries[country].scans++;
      if (scan?.converted) processed.countries[country].conversions++;

      // City stats
      const cityKey = `${city}, ${country}`;
      if (!processed?.cities?.[cityKey]) {
        processed.cities[cityKey] = {
          city,
          country,
          scans: 0,
          conversions: 0
        };
      }
      processed.cities[cityKey].scans++;
      if (scan?.converted) processed.cities[cityKey].conversions++;

      // Regional mapping (simplified)
      let region = 'Others';
      if (['US', 'CA', 'MX']?.includes(scan?.country_code)) region = 'North America';
      else if (['DE', 'FR', 'GB', 'IT', 'ES', 'NL', 'BE']?.includes(scan?.country_code)) region = 'Europe';
      else if (['CN', 'JP', 'IN', 'KR', 'SG']?.includes(scan?.country_code)) region = 'Asia';

      processed.regions[region].scans++;
      if (scan?.converted) processed.regions[region].conversions++;
    });

    // Calculate summary statistics
    processed.summary.totalCountries = Object.keys(processed?.countries)?.length;
    processed.summary.totalCities = Object.keys(processed?.cities)?.length;
    const totalConversions = scans?.filter(scan => scan?.converted)?.length || 0;
    processed.summary.conversionRate = scans?.length > 0 ? (totalConversions / scans?.length * 100) : 0;

    return processed;
  }

  // Get date range filter
  static getDateRangeFilter(dateRange) {
    const now = new Date();
    switch (dateRange) {
      case 'today':
        return new Date(now?.getFullYear(), now?.getMonth(), now?.getDate())?.toISOString();
      case 'last7days':
        return new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000)?.toISOString();
      case 'last30days': case'last30d':
        return new Date(now?.getTime() - 30 * 24 * 60 * 60 * 1000)?.toISOString();
      case 'last90days':
        return new Date(now?.getTime() - 90 * 24 * 60 * 60 * 1000)?.toISOString();
      default:
        return new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000)?.toISOString();
    }
  }
}